class CustomPagination extends HTMLElement {
  static get MAX_RESTORE_PAGES() { return 6; }
  static get MAX_CACHE_ENTRIES() { return 3; }

  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
    if (!this.productList) return;
    this.searchFeedback = this.closest('.section-collection-products-grid')?.querySelector('collection-search-feedback');
    this.loadMoreButton = this.querySelector('.btn--load-more');
    this.loader = this.querySelector('[data-pagination-loader]');
    this.paginationLinks = this.querySelector('[data-pagination-links]');
    this.infiniteScrollToggle = this.querySelector('[data-infinite-scroll-toggle]');
    this.isHybridPagination = this.dataset.type === 'pagination-infinite-scroll';
    this.isAutomaticInfiniteScroll = this.dataset.type === 'infinite-scroll';
    this.infiniteScrollEnabled = this.isAutomaticInfiniteScroll;
    this.pageResponseCache = new Map();
    this.abortController = new AbortController();
    this.suspended = false;
    this.restoring = false;
    this.failed = false;
    this.currentVisibleUrl = this.normalizeUrl(location.href);
    this.pageUrls = [this.currentVisibleUrl];
    this.nextPageUrl = this.getNextPageUrl();
    this.initialProductCount = this.productList.querySelectorAll('.grid__item').length;
    this.handleLoadMoreClick = () => this.loadNextPage();
    this.handleToggle = async (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.infiniteScrollEnabled) return;
      this.infiniteScrollEnabled = true;
      this.updateControls();
      await this.loadEarlierPages();
      if (!this.isConnected || this.suspended || !this.infiniteScrollEnabled || this.failed || this.atLimit) return;
      this.setupInfiniteScroll();
      if (this.getBoundingClientRect().top <= window.innerHeight + 600) this.loadNextPage();
    };
    this.handleSave = () => this.rememberPosition();
    this.loadMoreButton?.addEventListener('click', this.handleLoadMoreClick);
    this.infiniteScrollToggle?.addEventListener('click', this.handleToggle);
    window.addEventListener('pagehide', this.handleSave);
    this.setupChunkObserver();
    this.registerChunkStart(this.productList.firstElementChild, this.currentVisibleUrl);
    this.updateLimit();
    const saved = history.state?.mymaraPagination;
    this.ready = this.restoreVisitedPages(saved).then(async () => {
      if (this.isAutomaticInfiniteScroll && this.isConnected && !this.suspended
        && this.getPageNumber(this.pageUrls[0]) > 1) await this.loadEarlierPages();
    }).finally(() => {
      if (!this.isConnected) return;
      this.updateControls();
      if (this.infiniteScrollEnabled && !this.failed && !this.atLimit && this.nextPageUrl) this.setupInfiniteScroll();
    });
  }

  normalizeUrl(url) {
    if (!url) return null;
    const value = new URL(url, location.origin);
    value.searchParams.delete('section_id');
    value.searchParams.delete('pagination_section');
    value.searchParams.delete('drawer_section');
    return value.pathname + value.search;
  }

  getPageNumber(url) {
    const page = Number.parseInt(new URL(url, location.origin).searchParams.get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  getPageUrl(page) {
    const url = new URL(this.pageUrls[0], location.origin);
    if (page === 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    return this.normalizeUrl(url.toString());
  }

  getNextPageUrl() {
    const link = this.querySelector('[data-pagination-next-link]');
    return link ? this.normalizeUrl(link.href) : null;
  }

  getSectionRequestUrl(pageUrl) {
    const url = new URL(pageUrl, location.origin);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('pagination_section', '1');
    return url.toString();
  }

  async fetchHtml(url, controller) {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`Failed to load pagination page: ${response.status}`);
    const html = await response.text();
    if (controller.signal.aborted) throw new DOMException('Disconnected', 'AbortError');
    return html;
  }

  hasProductGrid(html) {
    return new RegExp(`<ul[^>]*\\sdata-id="${this.sectionId}"`).test(html);
  }

  async fetchPageResponse(pageUrl) {
    const controller = this.abortController;
    const key = this.normalizeUrl(pageUrl);
    if (this.pageResponseCache.has(key)) return this.pageResponseCache.get(key);
    let html = await this.fetchHtml(this.getSectionRequestUrl(key), controller);
    // The section picks its variant from the request URL parsed out of content_for_header, which Shopify
    // sometimes reports for a different request (e.g. the filter drawer). The full page always has the grid.
    if (!this.hasProductGrid(html)) html = await this.fetchHtml(key, controller);
    this.pageResponseCache.set(key, html);
    while (this.pageResponseCache.size > CustomPagination.MAX_CACHE_ENTRIES) {
      this.pageResponseCache.delete(this.pageResponseCache.keys().next().value);
    }
    return html;
  }

  parsePage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector(`ul[data-id="${this.sectionId}"]`);
    if (!grid || !grid.children.length) throw new Error('Pagination response has no product grid');
    return { doc, elements: Array.from(grid.children), products: grid.querySelectorAll('.grid__item').length };
  }

  updateLimit() {
    this.atLimit = !this.nextPageUrl;
    this.searchFeedback?.setReachedPage?.(this.getPageNumber(this.pageUrls[this.pageUrls.length - 1]), this.atLimit);
    if (this.atLimit) this.stopInfiniteScroll();
  }

  updateControls() {
    const canAppend = Boolean(this.nextPageUrl && !this.atLimit);
    const showPages = this.isHybridPagination
      ? !this.infiniteScrollEnabled || this.failed
      : this.isAutomaticInfiniteScroll ? this.failed : this.atLimit || this.failed || !this.nextPageUrl;
    if (this.paginationLinks) {
      this.paginationLinks.hidden = !showPages;
      this.paginationLinks.classList.remove('visually-hidden');
      this.paginationLinks.removeAttribute('aria-hidden');
      this.paginationLinks.querySelectorAll('a').forEach((link) => link.removeAttribute('tabindex'));
    }
    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = !canAppend || this.isAutomaticInfiniteScroll || this.isHybridPagination;
    }
    if (this.infiniteScrollToggle) {
      const hasEarlierPages = this.getPageNumber(this.pageUrls[0]) > 1;
      this.infiniteScrollToggle.hidden = this.infiniteScrollEnabled || (!canAppend && !hasEarlierPages);
      const showAllUrl = this.getPageUrl(1);
      if (showAllUrl) this.infiniteScrollToggle.href = showAllUrl;
      else this.infiniteScrollToggle.removeAttribute('href');
      this.infiniteScrollToggle.textContent = this.infiniteScrollToggle.dataset.enableLabel;
    }
    const hasVisibleControls = [this.paginationLinks, this.loadMoreButton, this.infiniteScrollToggle]
      .some((element) => element && !element.hidden);
    this.closest('.pagination-wrapper')?.classList.toggle('pagination-wrapper--empty', !hasVisibleControls);
  }

  setupInfiniteScroll() {
    if (this.scrollObserver || this.scrollFallbackActive || !this.isConnected || !this.nextPageUrl || this.atLimit) return;
    if (!('IntersectionObserver' in window)) {
      this.scrollFallbackActive = true;
      this.handleScrollFallback = () => {
        if (this.getBoundingClientRect().top <= window.innerHeight + 600) this.loadNextPage();
      };
      window.addEventListener('scroll', this.handleScrollFallback, { passive: true });
      window.addEventListener('resize', this.handleScrollFallback);
      this.handleScrollFallback();
      return;
    }
    this.scrollObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
    }, { rootMargin: '0px 0px 600px 0px', threshold: 0 });
    this.scrollObserver.observe(this);
  }

  stopInfiniteScroll() {
    this.scrollObserver?.disconnect();
    this.scrollObserver = null;
    if (this.scrollFallbackActive) {
      window.removeEventListener('scroll', this.handleScrollFallback);
      window.removeEventListener('resize', this.handleScrollFallback);
      this.scrollFallbackActive = false;
    }
    if (this.retryOnScroll) {
      window.removeEventListener('scroll', this.retryOnScroll);
      this.retryOnScroll = null;
    }
  }

  retryInfiniteScrollOnScroll() {
    if (!this.isAutomaticInfiniteScroll || this.retryOnScroll) return;
    this.retryOnScroll = () => {
      window.removeEventListener('scroll', this.retryOnScroll);
      this.retryOnScroll = null;
      if (!this.isConnected || this.suspended) return;
      this.failed = false;
      this.loadNextPage();
    };
    window.addEventListener('scroll', this.retryOnScroll, { passive: true });
  }

  setupChunkObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.chunkObserver = new IntersectionObserver((entries) => {
      if (this.restoring) return;
      const visible = entries.filter((entry) => entry.isIntersecting);
      visible.sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
      if (visible[0]) this.updateVisibleUrl(visible[0].target.dataset.paginationPageUrl);
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
  }

  registerChunkStart(element, url) {
    if (!element) return;
    element.dataset.paginationPageUrl = url;
    this.chunkObserver?.observe(element);
  }

  updateVisibleUrl(url) {
    if (this.suspended || !url || url === this.currentVisibleUrl) return;
    this.currentVisibleUrl = this.normalizeUrl(url);
    history.replaceState({
      ...history.state,
      searchParams: new URL(this.currentVisibleUrl, location.origin).search.slice(1)
    }, '', this.currentVisibleUrl + location.hash);
    this.rememberPosition();
  }

  rememberPosition() {
    if (!this.isConnected || this.suspended || this.restoring || !this.pageUrls?.length
      || this.normalizeUrl(location.href) !== this.currentVisibleUrl) return;
    const items = Array.from(this.productList.children);
    const index = items.findIndex((item) => item.getBoundingClientRect().bottom > 0);
    const item = items[index];
    const currentPageIndex = this.pageUrls.findIndex((page) => page === this.currentVisibleUrl);
    const firstSavedPageIndex = Math.max(0, currentPageIndex - CustomPagination.MAX_RESTORE_PAGES + 1);
    const firstSavedItemIndex = items.findIndex(
      (entry) => entry.dataset.paginationPageUrl === this.pageUrls[firstSavedPageIndex]
    );
    history.replaceState({
      ...history.state,
      mymaraPagination: {
        section: this.sectionId,
        url: this.normalizeUrl(location.href),
        pages: this.pageUrls.slice(firstSavedPageIndex, currentPageIndex + 1),
        infinite: this.infiniteScrollEnabled,
        anchorIndex: firstSavedItemIndex >= 0 ? Math.max(0, index - firstSavedItemIndex) : index,
        anchorTop: item?.getBoundingClientRect().top || 0,
        scrollY: window.scrollY
      }
    }, '');
  }

  updatePaginationLinks(doc) {
    const source = doc.querySelector('custom-pagination [data-pagination-links]');
    if (source) {
      if (this.paginationLinks) this.paginationLinks.replaceWith(source);
      else this.prepend(source);
    } else {
      this.paginationLinks?.remove();
    }
    this.paginationLinks = this.querySelector('[data-pagination-links]');
    this.nextPageUrl = this.getNextPageUrl();
  }

  async loadEarlierPages() {
    const firstPage = this.getPageNumber(this.pageUrls[0]);
    if (firstPage === 1) return;
    const controller = this.abortController;
    const anchor = this.productList.firstElementChild;
    const anchorTop = anchor?.getBoundingClientRect().top;
    this.restoring = true;
    this.setLoading(true);
    this.stopInfiniteScroll();
    try {
      const pages = [];
      for (let pageNumber = 1; pageNumber < firstPage; pageNumber += 3) {
        const urls = Array.from({ length: Math.min(3, firstPage - pageNumber) }, (_, index) => this.getPageUrl(pageNumber + index));
        const batch = await Promise.all(urls.map(async (url) => ({ ...this.parsePage(await this.fetchPageResponse(url)), url })));
        pages.push(...batch);
      }
      if (!this.isConnected || controller.signal.aborted || !this.infiniteScrollEnabled) return;
      const fragment = document.createDocumentFragment();
      pages.forEach((page) => page.elements.forEach((element) => fragment.appendChild(element)));
      this.productList.prepend(fragment);
      this.pageUrls.unshift(...pages.map((page) => page.url));
      this.chunkObserver?.disconnect();
      this.productList.querySelectorAll('[data-pagination-page-url]').forEach((element) => this.chunkObserver?.observe(element));
      pages.forEach((page) => this.registerChunkStart(page.elements[0], page.url));
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(this.productList);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!this.isConnected || controller.signal.aborted) return;
      if (anchor) window.scrollTo({ top: window.scrollY + anchor.getBoundingClientRect().top - anchorTop, behavior: 'instant' });
      this.failed = false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.failed = true;
        this.infiniteScrollEnabled = false;
        console.error('Error loading earlier products:', error);
      }
    } finally {
      if (controller !== this.abortController || !this.isConnected) return;
      this.restoring = false;
      this.setLoading(false);
      this.updateControls();
    }
  }

  async appendElements(elements, controller = this.abortController) {
    for (let index = 0; index < elements.length; index += 12) {
      if (!this.isConnected || controller.signal.aborted) return;
      const fragment = document.createDocumentFragment();
      elements.slice(index, index + 12).forEach((item) => fragment.appendChild(item));
      this.productList.appendChild(fragment);
      if (index + 12 < elements.length) await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(this.productList);
  }

  async loadNextPage() {
    if (!this.isConnected || this.suspended || this.hasAttribute('loading') || this.restoring || this.atLimit || !this.nextPageUrl) return;
    if (this.isHybridPagination && !this.infiniteScrollEnabled) return;
    const pageUrl = this.nextPageUrl;
    const controller = this.abortController;
    this.setLoading(true);
    try {
      const page = this.parsePage(await this.fetchPageResponse(pageUrl));
      if (!this.isConnected || controller.signal.aborted || (this.isHybridPagination && !this.infiniteScrollEnabled)) return;
      await this.appendElements(page.elements, controller);
      if (!this.isConnected || controller.signal.aborted) return;
      this.pageUrls.push(pageUrl);
      this.registerChunkStart(page.elements[0], pageUrl);
      this.updatePaginationLinks(page.doc);
      this.updateLimit();
      this.failed = false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.failed = true;
        this.stopInfiniteScroll();
        this.retryInfiniteScrollOnScroll();
        console.error('Error fetching products:', error);
      }
    } finally {
      if (controller !== this.abortController || !this.isConnected) return;
      this.setLoading(false);
      this.updateControls();
      this.searchFeedback?.checkEnd?.();
      if (this.infiniteScrollEnabled && !this.failed && !this.atLimit) this.setupInfiniteScroll();
      if (this.infiniteScrollEnabled && !this.failed && !this.atLimit && this.nextPageUrl
        && this.getBoundingClientRect().top <= window.innerHeight + 600) {
        window.requestAnimationFrame(() => this.loadNextPage());
      }
    }
  }

  async restoreVisitedPages(saved) {
    if (!saved || saved.section !== this.sectionId || saved.url !== this.currentVisibleUrl
      || !Array.isArray(saved.pages) || !saved.pages.includes(this.currentVisibleUrl)
      || saved.pages.length > CustomPagination.MAX_RESTORE_PAGES) return;
    const base = new URL(this.currentVisibleUrl, location.origin);
    base.searchParams.delete('page');
    base.searchParams.sort();
    const valid = saved.pages.every((value) => {
      const url = new URL(value, location.origin);
      url.searchParams.delete('page');
      url.searchParams.sort();
      return url.toString() === base.toString();
    });
    if (!valid) return;
    this.restoring = true;
    const controller = this.abortController;
    this.setLoading(true);
    const original = Array.from(this.productList.children);
    try {
      const pages = [];
      for (const url of saved.pages) {
        const page = url === this.currentVisibleUrl
          ? { elements: original, products: this.initialProductCount }
          : this.parsePage(await this.fetchPageResponse(url));
        pages.push({ ...page, url });
      }
      if (!this.isConnected || controller.signal.aborted) return;
      this.chunkObserver?.disconnect();
      this.productList.replaceChildren();
      for (const page of pages) {
        await this.appendElements(page.elements, controller);
        if (!this.isConnected || controller.signal.aborted) return;
        this.registerChunkStart(page.elements[0], page.url);
      }
      this.pageUrls = saved.pages.slice();
      const last = pages[pages.length - 1];
      if (last.doc) this.updatePaginationLinks(last.doc);
      this.infiniteScrollEnabled = saved.infinite;
      this.updateLimit();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!this.isConnected || controller.signal.aborted) return;
      const anchor = this.productList.children[saved.anchorIndex];
      window.scrollTo({
        top: anchor ? window.scrollY + anchor.getBoundingClientRect().top - saved.anchorTop : saved.scrollY,
        behavior: 'instant'
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error restoring visited products:', error);
    } finally {
      if (controller !== this.abortController || !this.isConnected) return;
      this.restoring = false;
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.toggleAttribute('loading', loading);
    this.loadMoreButton?.classList.toggle('loading', loading);
    this.loadMoreButton?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
    // Infinite scroll and the "show all" toggle have no visible button to carry the spinner
    if (this.loader) this.loader.hidden = !loading || Boolean(this.loadMoreButton && !this.loadMoreButton.hidden);
  }

  suspend() {
    this.suspended = true;
    this.abortController?.abort();
    this.stopInfiniteScroll();
    this.chunkObserver?.disconnect();
  }

  disconnectedCallback() {
    this.suspend();
    this.pageResponseCache?.clear();
    this.loadMoreButton?.removeEventListener('click', this.handleLoadMoreClick);
    this.infiniteScrollToggle?.removeEventListener('click', this.handleToggle);
    window.removeEventListener('pagehide', this.handleSave);
  }
}

if (!customElements.get('custom-pagination')) {
  customElements.define('custom-pagination', CustomPagination);
}
